import {
  getAllCourses,
  getCourseThemesSubCollection,
} from '@/firebase/courses-ts'
import { getHomePageTemplates } from '@/firebase/home-page-templates'
import {
  CoursePurpose,
  CourseSubject,
  ExamBoards,
  ExamLevel,
  HomePageTemplateStatus,
  IBookedCourse,
  ICourse,
  ICourseWithSchemaThemeDto,
  IHomePageTemplate,
  ISchemaThemeDto,
  UserRole,
} from '@/types/firebase'
import { CourseOverviewTabKey, IGenericError } from '@/types/shared'
import { notify } from '@/utils/index'
import { QueryConstraint, Timestamp, where } from 'firebase/firestore'
import { useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'
import {
  convertLinkPropToExamBoard,
  convertLinkPropToExamLevel,
  convertLinkPropToSubject,
} from '@/utils/courses'
import {
  CommonCourseSettingsFilter,
  generateExamBoardsSelectOptions,
  generateExamLevelGroupSingleOption,
} from '@/components/ui'
import SvgSpinnerBlue from '@/components/icons/spinner-blue'
import { CoursePreviewCard } from '@/components/page-partials/courses/components'
import { useSelector } from 'react-redux'
import authSelector from '@/redux/selectors/auth'
import {
  createBookedCourse,
  getBookedCoursesByUserIdAndCourseId,
  updateBookedCourse,
} from '@/firebase/booked-courses'
import { uniq } from 'lodash'

const host = process.env.NEXT_PUBLIC_HOST || ''

export const EmbeddedCoursesContent: React.FC = () => {
  const searchParams = useSearchParams()

  const examLevelSearchParam = searchParams?.get('examLevel')
  const convertedLinkPropToExamLevel = convertLinkPropToExamLevel(
    examLevelSearchParam ?? undefined,
  )

  const templateRouteId = searchParams?.get('templateRouteId')

  const subjectSearchParam = searchParams?.get('subject')

  // get all examBoards stringified search params
  const examBoardsSearchParams = searchParams?.getAll('examBoard')
  // transform params to Array or left it as undefined
  const parsedExamBoardsSearchParam: string[] | undefined =
    examBoardsSearchParams
      ? Array.isArray(examBoardsSearchParams)
        ? examBoardsSearchParams
        : [examBoardsSearchParams]
      : undefined

  // convert stringified params to ExamBoards enum values
  const convertedLinkPropsToExamBoards: ExamBoards[] =
    parsedExamBoardsSearchParam && parsedExamBoardsSearchParam.length
      ? parsedExamBoardsSearchParam.reduce((acc: ExamBoards[], next) => {
          const converted = convertLinkPropToExamBoard(next)

          if (!!converted && acc.includes(converted)) {
            return acc
          }

          if (!!converted) {
            return [...acc, converted]
          }

          return acc
        }, [])
      : []

  const [template, setTemplate] = useState<IHomePageTemplate>()
  const [coursesWithThemesDto, setCoursesWithThemesDto] =
    useState<ICourseWithSchemaThemeDto[]>()
  const [isLoading, setIsLoading] = useState(false)
  const [selectedExamBoards, setSelectedExamBoards] = useState<ExamBoards[]>(
    convertedLinkPropsToExamBoards,
  )
  const [selectedExamLevels, setSelectedExamLevels] = useState<
    ExamLevel[] | undefined
  >(
    convertedLinkPropToExamLevel
      ? generateExamLevelGroupSingleOption([convertedLinkPropToExamLevel])
          ?.value
      : undefined,
  )
  const [selectedSubject, setSelectedSubject] = useState<
    CourseSubject | undefined
  >(convertLinkPropToSubject(subjectSearchParam ?? undefined))

  const { user } = useSelector(authSelector)

  const getFilteredCourses = useCallback(
    async (
      templateRouteId: string | undefined,
      examLevels: ExamLevel[] | undefined,
      examBoards: ExamBoards[] | undefined,
    ) => {
      try {
        // get template
        if (templateRouteId) {
          setIsLoading(true)

          // generate query constraints
          const queryConstraints: QueryConstraint[] = [
            where('status', '==', HomePageTemplateStatus.Active),
            where('routeId', '==', templateRouteId),
          ]

          // get templates
          const templates: IHomePageTemplate[] = await getHomePageTemplates(
            queryConstraints,
          )

          // select template
          const targetTemplate = templates[0]

          if (templates.length > 1) {
            console.error(
              `Unexpected behavior! Found more than one Home Page Template with routeId: ${templateRouteId}. Route Id should be unique for each template  :>> `,
            )
          }

          // set template
          targetTemplate && setTemplate(targetTemplate)
        }

        // get courses
        setIsLoading(true)

        // generate courses query constraints
        const queryConstraints: QueryConstraint[] = [
          where('publishAt', '==', true),
          where('coursePurpose', '==', CoursePurpose.Basic),
        ]

        // add subject constraint
        if (selectedSubject) {
          queryConstraints.push(where('subject', '==', selectedSubject))
        }

        // add examBoards constraint
        if (examBoards && !!examBoards.length) {
          queryConstraints.push(
            where('examBoards', 'array-contains-any', examBoards),
          )
        }

        // get courses
        let courses: ICourse[] = await getAllCourses(queryConstraints)

        // filter out by selected examLevels
        if (examLevels && !!examLevels.length) {
          courses = courses.filter((c) => examLevels.includes(c.examLevel))
        }

        const courseWithSchemaThemesPromises: Promise<ICourseWithSchemaThemeDto>[] =
          courses.map(async (course) => {
            const schemaThemeDto: ISchemaThemeDto[] =
              await getCourseThemesSubCollection(course.id)

            const courseWithSchemaThemeDto: ICourseWithSchemaThemeDto = {
              course,
              courseSchemaTheme: schemaThemeDto,
            }

            return courseWithSchemaThemeDto
          })

        const coursesWithThemesDto: ICourseWithSchemaThemeDto[] =
          await Promise.all(courseWithSchemaThemesPromises)

        // set courses
        setCoursesWithThemesDto(coursesWithThemesDto)
      } catch (error: unknown) {
        console.error(
          'error getFilteredCourses :>> ',
          (error as IGenericError)?.message || error,
        )

        if (error && typeof error === 'object' && 'message' in error) {
          notify('error', error.message)
        }
      } finally {
        setIsLoading(false)
      }
    },
    [selectedSubject],
  )

  useEffect(() => {
    getFilteredCourses(
      templateRouteId ?? undefined,
      selectedExamLevels,
      selectedExamBoards,
    )
  }, [
    selectedExamLevels,
    getFilteredCourses,
    selectedExamBoards,
    templateRouteId,
  ])

  const handleCourseClick = useCallback(
    async (course: ICourse, examBoard: ExamBoards) => {
      try {
        setIsLoading(true)

        let bookedCourseId: string | undefined = undefined

        const isStudent = !!user && user.role === UserRole.Student

        // handle case if user is already authorized
        if (isStudent) {
          // get student booked courses
          const bookedCourses: IBookedCourse[] =
            await getBookedCoursesByUserIdAndCourseId(user.uid, course.id)

          // find target booked course
          let targetBookedCourse: IBookedCourse | undefined =
            bookedCourses.find((b) => b.courseId === course.id)

          // create booked course document if no one found
          if (!targetBookedCourse) {
            targetBookedCourse = await createBookedCourse({
              userId: user.uid,
              course,
              useCalibratedMMR: true,
              partialBookedCourse: {
                isSelected: true, // set bookedCourse as selected by default on course overview page redirect
                selectionDate: Timestamp.now(),
                lastInteractionDate: Timestamp.now(),
                examBoard,
              },
            })
          } else {
            // update booked course examBoard depending on selection
            await updateBookedCourse(targetBookedCourse.id, {
              isSelected: true, // set bookedCourse as selected by default on course overview page redirect
              selectionDate: Timestamp.now(),
              lastInteractionDate: Timestamp.now(),
              examBoard,
            })
          }

          // set booked course id for query parameter
          bookedCourseId = targetBookedCourse.id
        }

        // if user is authorized and user role is student - then open
        // students course-overview, otherwise move to noAuth flow course-overview
        let path =
          isStudent && !!bookedCourseId
            ? `${user.role}/${course.id}`
            : `courses/${course.id}`

        const params = new URLSearchParams()
        params.append('tab', CourseOverviewTabKey.Overview)
        params.append('useWpRedirect', 'true')

        if (isStudent && bookedCourseId) {
          params.append('bookedId', bookedCourseId)
        }

        if (templateRouteId) {
          params.append('templateRouteId', templateRouteId)
        }

        if (examBoard) {
          params.append('examBoard', examBoard)
        }

        const queryString = params.toString()

        const href = `${host}/${path}?${queryString}`

        // open in new tab
        window.open(href, '_blank')
      } catch (error: unknown) {
        console.error(
          'error handleCourseClick :>> ',
          (error as IGenericError)?.message || error,
        )

        if (error && typeof error === 'object' && 'message' in error) {
          notify('error', error.message)
        }
      } finally {
        setIsLoading(false)
      }
    },
    [templateRouteId, user],
  )

  // generate examBoards select options depending on course.examBoards values from coursesWithThemesDto found
  const examBoardsFromCourses =
    coursesWithThemesDto && coursesWithThemesDto.length
      ? uniq(coursesWithThemesDto.flatMap((c) => c.course.examBoards))
      : []

  return (
    <div className="min-h-screen w-[100%] bg-[#F7F7F7]">
      <div className={`w-full min-h-[100vh] flex flex-col justify-between`}>
        <div className="flex flex-col grow w-[100%]">
          <div className="h-full w-full max-w-7xl mx-auto flex flex-col items-center grow gap-4 px-4 py-8 lg:py-10 bg-[#F7F7F7]">
            {/* Courses Filter */}
            <CommonCourseSettingsFilter
              containerClassName="w-full lg:px-2 flex flex-col lg:flex-row items-center justify-end gap-2"
              initialExamLevels={selectedExamLevels}
              examLevelChangeHandler={setSelectedExamLevels}
              examLevelSelectClassName="w-full lg:w-[200px] rounded-md select-inner-block text-left h-10"
              initialExamBoards={selectedExamBoards}
              examBoardsChangeHandler={setSelectedExamBoards}
              examBoardSelectClassName="w-full lg:w-auto sm:min-w-[200px] rounded-md select-inner-block text-left h-10"
              examBoardsSelectOptions={
                examBoardsFromCourses.length
                  ? generateExamBoardsSelectOptions(examBoardsFromCourses)
                  : undefined
              }
              initialSubject={selectedSubject}
              subjectChangeHandler={setSelectedSubject}
              subjectSelectClassName="w-full lg:w-[200px] rounded-md select-inner-block text-left h-10"
            />

            {/* Divider */}
            <div
              id="divider"
              className="h-[1px] w-[99%] bg-v8-line self-center"
            ></div>

            {isLoading ? (
              <div className="h-full w-full h-max min-h-[300px] flex flex-col grow items-center justify-center">
                <SvgSpinnerBlue withyMargin={false} classess="w-6 h-6" />
              </div>
            ) : (
              <>
                {coursesWithThemesDto && (
                  <>
                    {!coursesWithThemesDto.length ? (
                      <div className="h-full w-full h-max min-h-[300px] flex flex-col grow items-center justify-center">
                        <p className="text-v8-mut">Courses not found</p>
                      </div>
                    ) : (
                      <>
                        {/* Courses List */}
                        <ul
                          className={`w-full flex ${
                            coursesWithThemesDto.length < 3
                              ? 'flex items-center justify-center gap-3'
                              : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-3'
                          } flex-wrap gap-0`}
                        >
                          {coursesWithThemesDto.map((courseData) => {
                            return (
                              <li
                                key={courseData.course.id}
                                className="w-full md:basis-1/2 lg:basis-1/3"
                              >
                                <CoursePreviewCard
                                  course={courseData.course}
                                  templateRouteId={
                                    !!templateRouteId
                                      ? templateRouteId
                                      : undefined
                                  }
                                  additionalCourseClickHandler={
                                    handleCourseClick
                                  }
                                />
                              </li>
                            )
                          })}
                        </ul>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
